import { useStore, newControllerId } from '../state/store'
import { defaultEdgeBand } from '../core/types'
import type { ChannelId, Controller, ControllerKind, DocumentModel, FalloffKind, SingleSide, Vec2 } from '../core/types'
import { setSyncAcrossChannels } from '../core/curves'
import { ValueField, SliderField, useFieldOps } from './ValueField'
import { useT } from '../i18n'
import { EMBED_WARN_BYTES, pickImageFile, type ImportedImage } from './imageAssets'
import { IconCurve, IconImport, IconLine, IconPoint, IconPolyline, IconTrash } from './icons'

export const CHANNELS: { id: ChannelId; label: string }[] = [
  { id: 'size', label: 'Size' },
  { id: 'spacing', label: 'Spacing' },
  { id: 'angle', label: 'Angle' },
  { id: 'shape', label: 'Shape' },
]

export const CHANNEL_RANGES: Record<ChannelId, { min: number; max: number; unit: string }> = {
  size: { min: 0.2, max: 40, unit: 'mm' },
  spacing: { min: 0.5, max: 60, unit: 'mm' },
  angle: { min: -180, max: 180, unit: 'deg' },
  shape: { min: 0, max: 1, unit: '' },
}

export function SidePanel() {
  return (
    <aside className="side-panel" aria-label="Design settings">
      <LayoutSection />
      <ShapeSection />
      <BoundarySection />
      <ChannelsSection />
      <BackgroundSection />
      <ExportSection />
    </aside>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="panel-section">
      <h3>{title}</h3>
      {children}
    </section>
  )
}

function LayoutSection() {
  const doc = useStore((s) => s.doc)
  const commit = useStore((s) => s.commit)
  const mode = doc.layout.mode

  const angleAOps = useFieldOps('Set hatch angle A', (d, v) => {
    d.layout.crosshatch.angleA = v
  })
  const angleBOps = useFieldOps('Set hatch angle B', (d, v) => {
    d.layout.crosshatch.angleB = v
  })
  const jitterOps = useFieldOps('Set jitter', (d, v) => {
    d.layout.jitter = v
  })
  const crossRowOps = useFieldOps('Set cross-row spin', (d, v) => {
    d.layout.grid.crossRowSpin = v
  })
  const spinByOps = useFieldOps('Set spin by', (d, v) => {
    d.layout.grid.spinBy = v
  })
  const cornerOps = useFieldOps('Set corner radius', (d, v) => {
    d.layout.voronoi.cornerRadius = v
  })
  const gridAngleOps = useFieldOps('Set lattice angle', (d, v) => {
    d.layout.grid.angle = v
  })
  const marginOps = useFieldOps('Set edge margin', (d, v) => {
    d.clipMargin = v
  })

  const t = useT()
  return (
    <Section title={t('section.layout', 'Layout')}>
      {mode === 'grid' && (
        <>
          <label className="row">
            <span className="row-label">Pattern</span>
            <select
              value={doc.layout.grid.pattern}
              onChange={(e) =>
                commit('Set grid pattern', (d) => {
                  d.layout.grid.pattern = e.target.value as 'rect' | 'hex'
                })
              }
            >
              <option value="hex">Hexagonal</option>
              <option value="rect">Rectangular</option>
            </select>
          </label>
          <ValueField
            label="Row spin"
            value={doc.layout.grid.crossRowSpin}
            min={-90}
            max={90}
            unit="deg"
            title="Cumulative rotation added per row (visible on non-circular shapes)"
            {...crossRowOps}
          />
          <ValueField
            label="Spin by"
            value={doc.layout.grid.spinBy}
            min={-45}
            max={45}
            step={0.5}
            unit="deg"
            title="Rotation increment per instance (visible on non-circular shapes)"
            {...spinByOps}
          />
          <ValueField
            label="Lattice"
            value={doc.layout.grid.angle}
            min={-90}
            max={90}
            unit="deg"
            title="Rotate the whole grid lattice"
            {...gridAngleOps}
          />
        </>
      )}
      {mode === 'crosshatch' && (
        <>
          <ValueField label="Angle A" value={doc.layout.crosshatch.angleA} min={-90} max={90} unit="deg" {...angleAOps} />
          <ValueField label="Angle B" value={doc.layout.crosshatch.angleB} min={-90} max={90} unit="deg" {...angleBOps} />
        </>
      )}
      {(mode === 'spiral' || mode === 'concentric' || mode === 'path') && (
        <label className="row mini">
          <input
            type="checkbox"
            checked={doc.layout.rotateToPath}
            onChange={(e) =>
              commit('Toggle rotate to path', (d) => {
                d.layout.rotateToPath = e.target.checked
              })
            }
          />
          Rotate shapes to path
        </label>
      )}
      {mode === 'path' && <PathsBlock />}
      {mode !== 'voronoi' && mode !== 'flowlines' && (
        <ValueField
          label="Jitter"
          value={doc.layout.jitter}
          min={0}
          max={20}
          step={0.1}
          unit="mm"
          title="Seeded random offset per placement"
          {...jitterOps}
        />
      )}
      <ValueField
        label="Margin"
        value={doc.clipMargin}
        min={0}
        max={30}
        step={0.5}
        unit="mm"
        title="Minimum clearance between the pattern and the boundary edge (polygon boundaries: applied by dropping, not clipping)"
        {...marginOps}
      />
      <ScaleRow />
      {mode !== 'voronoi' && mode !== 'flowlines' && (
        <label className="row">
          <span className="row-label">Edges</span>
          <select
            value={doc.clip}
            onChange={(e) =>
              commit('Set edge handling', (d) => {
                d.clip = e.target.value as typeof d.clip
                // Uniform wants a visible band; seed it at 2% when unset.
                if (d.clip === 'uniform' && d.clipMargin <= 0) {
                  d.clipMargin = defaultEdgeBand(d.canvas.width, d.canvas.height)
                }
              })
            }
          >
            <option value="uniform">Uniform edge band</option>
            <option value="dropTouching">Drop touching edge</option>
            <option value="dropOutside">Drop centered outside</option>
            <option value="clip">Clip at edge</option>
          </select>
        </label>
      )}
      {mode === 'voronoi' && (
        <>
          <ValueField
            label="Corner rad"
            value={doc.layout.voronoi.cornerRadius}
            min={0}
            max={10}
            step={0.1}
            unit="mm"
            title="Round each cell corner with an arc"
            {...cornerOps}
          />
          <p className="hint">
            Voronoi: the Spacing channel sets cell density, the Size channel sets crack width.
          </p>
        </>
      )}
      {mode === 'flowlines' && (
        <>
          <label className="row">
            <span className="row-label">Caps</span>
            <select
              value={doc.layout.flow.cap}
              onChange={(e) =>
                commit('Set flow caps', (d) => {
                  d.layout.flow.cap = e.target.value as 'round' | 'flat'
                })
              }
            >
              <option value="round">Round</option>
              <option value="flat">Flat</option>
            </select>
          </label>
          <p className="hint">
            Flow lines follow the Angle field. Spacing sets line separation, Size sets line
            width; lines meeting the boundary are cut along it.
          </p>
        </>
      )}
    </Section>
  )
}

// Proportional scale lives in its own modal; this row just opens it.
function ScaleRow() {
  const setOverlay = useStore((s) => s.setOverlay)
  const canvas = useStore((s) => s.doc.canvas)
  return (
    <div className="row" title="Uniformly rescale the whole design: canvas, boundary, controllers, sizes, spacing">
      <span className="row-label">Scale</span>
      <button className="icon-btn" onClick={() => setOverlay('scale')}>
        Scale design ({canvas.width} x {canvas.height} mm)
      </button>
    </div>
  )
}

function PathsBlock() {
  const doc = useStore((s) => s.doc)
  const commit = useStore((s) => s.commit)
  const activeChannel = useStore((s) => s.activeChannel)
  const pendingAdd = useStore((s) => s.pendingAdd)
  const setPendingAdd = useStore((s) => s.setPendingAdd)
  const selectedPath = useStore((s) => s.selectedPath)
  const setSelectedPath = useStore((s) => s.setSelectedPath)
  const paths = doc.layout.paths

  const toggleAdd = (kind: 'curve' | 'polyline') =>
    setPendingAdd(
      pendingAdd?.target === 'path' && pendingAdd.kind === kind
        ? null
        : { target: 'path', channel: activeChannel, kind, firstPoint: null, nodes: [] },
    )

  return (
    <>
      <div className="row add-row">
        <button
          className={pendingAdd?.target === 'path' && pendingAdd.kind === 'curve' ? 'icon-btn active' : 'icon-btn'}
          onClick={() => toggleAdd('curve')}
          title="Draw a curved path"
        >
          <IconCurve size={14} />
          Curve
        </button>
        <button
          className={pendingAdd?.target === 'path' && pendingAdd.kind === 'polyline' ? 'icon-btn active' : 'icon-btn'}
          onClick={() => toggleAdd('polyline')}
          title="Draw a straight-segment path"
        >
          <IconPolyline size={14} />
          Poly
        </button>
      </div>
      {pendingAdd?.target === 'path' && (
        <p className="hint">Click to add nodes. Double-click or Enter finishes, Esc cancels.</p>
      )}
      {paths.length === 0 && pendingAdd?.target !== 'path' && (
        <p className="hint">Draw one or more paths; shapes step along them by the Spacing field.</p>
      )}
      {paths.map((p, i) => (
        <div
          key={p.id}
          className={selectedPath === p.id ? 'controller selected' : 'controller'}
          onClick={() => setSelectedPath(p.id)}
        >
          <div className="controller-head">
            <span className="controller-kind">
              {p.kind === 'curve' ? <IconCurve size={13} /> : <IconPolyline size={13} />}
              Path {i + 1}
              <span className="tab-badge">{p.nodes.length} nodes</span>
            </span>
            <button
              className="danger icon-btn quiet"
              aria-label="Delete path"
              onClick={(e) => {
                e.stopPropagation()
                commit('Delete path', (d) => {
                  d.layout.paths = d.layout.paths.filter((x) => x.id !== p.id)
                })
                if (selectedPath === p.id) setSelectedPath(null)
              }}
            >
              <IconTrash size={13} />
            </button>
          </div>
        </div>
      ))}
    </>
  )
}

function ShapeParams({ target }: { target: 'shape' | 'shapeB' }) {
  const doc = useStore((s) => s.doc)
  const commit = useStore((s) => s.commit)
  const setOverlay = useStore((s) => s.setOverlay)
  const shape = doc[target]
  const name = target === 'shape' ? 'shape' : 'morph target'

  const slotOps = useFieldOps(`Set ${name} slot ratio`, (d, v) => {
    d[target].slotRatio = v
  })
  const sidesOps = useFieldOps(`Set ${name} sides`, (d, v) => {
    d[target].sides = Math.round(v)
  })
  const pointsOps = useFieldOps(`Set ${name} star points`, (d, v) => {
    d[target].starPoints = Math.round(v)
  })
  const innerOps = useFieldOps(`Set ${name} star inner ratio`, (d, v) => {
    d[target].starInner = v
  })

  return (
    <>
      <label className="row">
        <span className="row-label">Kind</span>
        <select
          value={shape.kind}
          onChange={(e) =>
            commit(`Set ${name} kind`, (d) => {
              d[target].kind = e.target.value as typeof d.shape.kind
            })
          }
        >
          <option value="circle">Circle</option>
          <option value="slot">Slot</option>
          <option value="polygon">Polygon</option>
          <option value="star">Star</option>
          {target === 'shape' && <option value="custom">Custom</option>}
        </select>
      </label>
      {shape.kind === 'custom' && target === 'shape' && (
        <div className="row">
          <button className="icon-btn" onClick={() => setOverlay('shapeEditor')}>
            Edit custom shape
          </button>
          <span className="tab-badge">{doc.customShape.layers.length} layers</span>
        </div>
      )}
      {shape.kind === 'slot' && (
        <ValueField label="Width ratio" value={shape.slotRatio} min={0.05} max={0.95} step={0.05} {...slotOps} />
      )}
      {shape.kind === 'polygon' && (
        <ValueField label="Sides" value={shape.sides} min={3} max={12} step={1} {...sidesOps} />
      )}
      {shape.kind === 'star' && (
        <>
          <ValueField label="Points" value={shape.starPoints} min={3} max={12} step={1} {...pointsOps} />
          <ValueField label="Inner ratio" value={shape.starInner} min={0.1} max={0.95} step={0.05} {...innerOps} />
        </>
      )}
    </>
  )
}

function ShapeSection() {
  const t = useT()
  const doc = useStore((s) => s.doc)
  const hasShapeField =
    doc.channels.shape.base > 0 ||
    doc.channels.shape.controllers.length > 0 ||
    doc.channels.shape.image?.enabled

  if (doc.layout.mode === 'voronoi' || doc.layout.mode === 'flowlines') return null
  return (
    <Section title={t('section.shape', 'Shape')}>
      <ShapeParams target="shape" />
      {doc.shape.kind === 'circle' && !hasShapeField && (
        <p className="hint">Circles stay analytic in STEP exports (exact cylinders).</p>
      )}
      <h3 style={{ marginTop: 12 }}>{t('section.morph', 'Morph target')}</h3>
      <ShapeParams target="shapeB" />
      <p className="hint">
        The Shape channel (0 to 1) morphs each hole from the shape above toward this target.
        Morphed instances leave the analytic-circle fast path.
      </p>
    </Section>
  )
}

function BoundarySection() {
  const t = useT()
  const doc = useStore((s) => s.doc)
  const commit = useStore((s) => s.commit)
  const activeChannel = useStore((s) => s.activeChannel)
  const pendingAdd = useStore((s) => s.pendingAdd)
  const setPendingAdd = useStore((s) => s.setPendingAdd)
  const b = doc.boundary

  const widthOps = useFieldOps('Set boundary width', (d, v) => {
    d.boundary.width = v
  })
  const heightOps = useFieldOps('Set boundary height', (d, v) => {
    d.boundary.height = v
  })
  const radiusOps = useFieldOps('Set boundary radius', (d, v) => {
    d.boundary.radius = v
  })
  const rectCornerOps = useFieldOps('Set boundary corner radius', (d, v) => {
    d.boundary.cornerRadius = v
  })

  const drawing = pendingAdd?.target === 'boundary'

  return (
    <Section title={t('section.boundary', 'Boundary')}>
      <label className="row">
        <span className="row-label">Kind</span>
        <select
          value={b.kind}
          onChange={(e) =>
            commit('Set boundary kind', (d) => {
              d.boundary.kind = e.target.value as typeof d.boundary.kind
            })
          }
        >
          <option value="rect">Rectangle</option>
          <option value="circle">Circle</option>
          <option value="polygon">Polygon</option>
        </select>
      </label>
      {b.kind === 'rect' && (
        <>
          <ValueField label="Width" value={b.width} min={2} max={2000} unit="mm" {...widthOps} />
          <ValueField label="Height" value={b.height} min={2} max={2000} unit="mm" {...heightOps} />
          <ValueField
            label="Corner rad"
            value={b.cornerRadius}
            min={0}
            max={100}
            step={0.5}
            unit="mm"
            title="Round the panel corners"
            {...rectCornerOps}
          />
        </>
      )}
      {b.kind === 'circle' && (
        <ValueField label="Radius" value={b.radius} min={1} max={1000} unit="mm" {...radiusOps} />
      )}
      {b.kind === 'polygon' && (
        <>
          <div className="row">
            <button
              className={drawing ? 'icon-btn active' : 'icon-btn'}
              onClick={() =>
                setPendingAdd(
                  drawing
                    ? null
                    : { target: 'boundary', channel: activeChannel, kind: 'polyline', firstPoint: null, nodes: [] },
                )
              }
            >
              <IconPolyline size={14} />
              {b.nodes.length >= 3 ? 'Redraw boundary' : 'Draw boundary'}
            </button>
            {b.nodes.length >= 3 && <span className="tab-badge">{b.nodes.length} nodes</span>}
          </div>
          {drawing && (
            <p className="hint">Click to add corners. Double-click or Enter closes the outline, Esc cancels.</p>
          )}
          {!drawing && b.nodes.length < 3 && (
            <p className="hint">No outline yet: the rectangle is used until you draw one.</p>
          )}
          {!drawing && b.nodes.length >= 3 && (
            <p className="hint">Drag the boundary corners on canvas; double-click inserts or removes one.</p>
          )}
        </>
      )}
    </Section>
  )
}

function ChannelsSection() {
  const t = useT()
  const doc = useStore((s) => s.doc)
  const activeChannel = useStore((s) => s.activeChannel)
  const setActiveChannel = useStore((s) => s.setActiveChannel)
  const commit = useStore((s) => s.commit)
  const live = useStore((s) => s.live)
  const beginGesture = useStore((s) => s.beginGesture)
  const endGesture = useStore((s) => s.endGesture)
  const pendingAdd = useStore((s) => s.pendingAdd)
  const setPendingAdd = useStore((s) => s.setPendingAdd)
  const selectedController = useStore((s) => s.selectedController)
  const setSelectedController = useStore((s) => s.setSelectedController)

  const set = doc.channels[activeChannel]
  const range = CHANNEL_RANGES[activeChannel]
  const channelLabel = CHANNELS.find((c) => c.id === activeChannel)?.label ?? activeChannel

  return (
    <Section title={t('section.channels', 'Gradient channels')}>
      <div className="tabs" role="tablist" aria-label="Gradient channel">
        {CHANNELS.map((c) => (
          <button
            key={c.id}
            role="tab"
            aria-selected={activeChannel === c.id}
            className={activeChannel === c.id ? 'tab active' : 'tab'}
            style={{ ['--tab-color' as string]: `var(--ch-${c.id})` }}
            onClick={() => setActiveChannel(c.id)}
          >
            <span className="tab-dot" />
            {t(`channel.${c.id}`, c.label)}
            {doc.channels[c.id].controllers.length > 0 && (
              <span className="tab-badge">{doc.channels[c.id].controllers.length}</span>
            )}
          </button>
        ))}
      </div>

      <SliderField
        label="Base"
        value={set.base}
        min={range.min}
        max={range.max}
        unit={` ${range.unit}`}
        onStart={() => beginGesture(`Adjust base ${channelLabel.toLowerCase()}`)}
        onEnd={endGesture}
        onLive={(v) =>
          live((d) => {
            d.channels[activeChannel].base = v
          })
        }
      />

      <div className="row add-row">
        {(
          [
            { kind: 'point', label: 'Point', icon: IconPoint },
            { kind: 'line', label: 'Line', icon: IconLine },
            { kind: 'curve', label: 'Curve', icon: IconCurve },
            { kind: 'polyline', label: 'Poly', icon: IconPolyline },
          ] as { kind: ControllerKind; label: string; icon: typeof IconPoint }[]
        ).map((k) => (
          <button
            key={k.kind}
            className={pendingAdd?.kind === k.kind ? 'icon-btn active' : 'icon-btn'}
            title={`Add ${k.kind} controller`}
            onClick={() =>
              setPendingAdd(
                pendingAdd?.kind === k.kind && pendingAdd.target === 'controller'
                  ? null
                  : { target: 'controller', channel: activeChannel, kind: k.kind, firstPoint: null, nodes: [] },
              )
            }
          >
            <k.icon size={14} />
            {k.label}
          </button>
        ))}
      </div>
      {pendingAdd && (
        <p className="hint">
          {pendingAdd.kind === 'point'
            ? 'Click on the canvas to place the point controller. Esc cancels.'
            : pendingAdd.kind === 'line'
              ? pendingAdd.firstPoint
                ? 'Click to set the line end point.'
                : 'Click to set the line start point. Esc cancels.'
              : 'Click to add nodes. Double-click or Enter finishes, Esc cancels.'}
        </p>
      )}

      {set.controllers.length > 1 && (
        <div className="row batch-row" title="Batch edit every controller on this channel">
          <ValueField
            label="Value all"
            compact
            value={set.controllers[0].value}
            min={range.min}
            max={range.max}
            onCommit={(v) =>
              commit('Unify controller values', (d) => {
                for (const c of d.channels[activeChannel].controllers) c.value = v
              })
            }
          />
          <ValueField
            label="Range all"
            compact
            value={set.controllers[0].radius}
            min={1}
            max={500}
            onCommit={(v) =>
              commit('Unify influence ranges', (d) => {
                for (const c of d.channels[activeChannel].controllers) c.radius = v
              })
            }
          />
        </div>
      )}

      <ImageBlock channel={activeChannel} range={range} />

      {set.controllers.length === 0 && (
        <p className="hint">
          No controllers on this channel yet. Drop a point or line to pull {channelLabel.toLowerCase()}{' '}
          toward a target value near it.
        </p>
      )}
      {set.controllers.map((c) => (
        <ControllerCard
          key={c.id}
          controller={c}
          selected={selectedController === c.id}
          range={range}
          onSelect={() => setSelectedController(c.id)}
          onChange={(label, up) =>
            commit(label, (d) => {
              const target = d.channels[activeChannel].controllers.find((x) => x.id === c.id)
              if (target) up(target)
            })
          }
          onDelete={() =>
            commit('Delete controller', (d) => {
              const list = d.channels[activeChannel].controllers
              d.channels[activeChannel].controllers = list.filter((x) => x.id !== c.id)
            })
          }
          onToggleSync={(on) =>
            commit(on ? 'Sync controller across channels' : 'Unsync controller', (d) => {
              setSyncAcrossChannels(d, activeChannel, c.id, on)
            })
          }
        />
      ))}
    </Section>
  )
}

export function ControllerFields({
  controller: c,
  range,
  compact,
  onChange,
  onToggleSync,
}: {
  controller: Controller
  range: { min: number; max: number; unit: string }
  compact?: boolean
  onChange: (label: string, up: (c: Controller) => void) => void
  onToggleSync: (on: boolean) => void
}) {
  const lineLike = c.kind !== 'point'
  return (
    <>
      <ValueField
        label="Target"
        value={c.value}
        min={range.min}
        max={range.max}
        unit={range.unit}
        compact={compact}
        onCommit={(v) => onChange('Set controller target', (x) => (x.value = v))}
      />
      <ValueField
        label="Influence"
        value={c.radius}
        min={1}
        max={500}
        unit="mm"
        compact={compact}
        onCommit={(v) => onChange('Set controller influence', (x) => (x.radius = v))}
      />
      <label className="row">
        <span className="row-label">Falloff</span>
        <select
          value={c.falloff}
          onChange={(e) => onChange('Set controller falloff', (x) => (x.falloff = e.target.value as FalloffKind))}
        >
          <option value="linear">Linear</option>
          <option value="smooth">Smooth</option>
          <option value="sharp">Sharp</option>
          <option value="soft">Soft</option>
        </select>
      </label>
      {lineLike && (
        <label className="row" title="Limit influence to one side, relative to the start-to-end direction">
          <span className="row-label">Side</span>
          <select
            value={c.singleSide}
            onChange={(e) => onChange('Set influence side', (x) => (x.singleSide = e.target.value as SingleSide))}
          >
            <option value="off">Both sides</option>
            <option value="left">Left only</option>
            <option value="right">Right only</option>
          </select>
        </label>
      )}
      <div className="row option-row">
        <label className="mini">
          <input
            type="checkbox"
            checked={c.hardEdge}
            onChange={(e) => onChange('Toggle hard edge', (x) => (x.hardEdge = e.target.checked))}
          />
          Hard edge
        </label>
        {lineLike && (
          <label className="mini" title="Extend the first and last segments to infinity">
            <input
              type="checkbox"
              checked={c.infinite}
              onChange={(e) => onChange('Toggle infinite extension', (x) => (x.infinite = e.target.checked))}
            />
            Infinite
          </label>
        )}
        <label
          className="mini"
          title="Share this controller's geometry with the other channels; each channel keeps its own value"
        >
          <input
            type="checkbox"
            checked={c.syncGroup !== null}
            onChange={(e) => onToggleSync(e.target.checked)}
          />
          Sync
        </label>
      </div>
    </>
  )
}

export function controllerIcon(kind: ControllerKind, size = 13) {
  switch (kind) {
    case 'point':
      return <IconPoint size={size} />
    case 'line':
      return <IconLine size={size} />
    case 'curve':
      return <IconCurve size={size} />
    case 'polyline':
      return <IconPolyline size={size} />
  }
}

function ControllerCard({
  controller: c,
  selected,
  range,
  onSelect,
  onChange,
  onDelete,
  onToggleSync,
}: {
  controller: Controller
  selected: boolean
  range: { min: number; max: number; unit: string }
  onSelect: () => void
  onChange: (label: string, up: (c: Controller) => void) => void
  onDelete: () => void
  onToggleSync: (on: boolean) => void
}) {
  return (
    <div className={selected ? 'controller selected' : 'controller'} onClick={onSelect}>
      <div className="controller-head">
        <span className="controller-kind">
          {controllerIcon(c.kind)}
          {c.kind}
        </span>
        <label className="mini">
          <input
            type="checkbox"
            checked={c.enabled}
            onChange={(e) => onChange('Toggle controller', (x) => (x.enabled = e.target.checked))}
          />
          on
        </label>
        <button className="danger icon-btn quiet" onClick={onDelete} aria-label="Delete controller">
          <IconTrash size={13} />
        </button>
      </div>
      <ControllerFields controller={c} range={range} onChange={onChange} onToggleSync={onToggleSync} />
    </div>
  )
}

// Default image mapping per channel: photos become halftones out of the box
// (dark areas grow holes / tighten spacing).
function imageDefaults(channel: ChannelId, base: number): { lo: number; hi: number } {
  switch (channel) {
    case 'size':
      return { lo: Math.min(40, base * 2), hi: Math.max(0.4, base * 0.3) }
    case 'spacing':
      return { lo: Math.max(0.5, base * 0.7), hi: Math.min(60, base * 1.6) }
    case 'angle':
      return { lo: 0, hi: 90 }
    case 'shape':
      return { lo: 1, hi: 0 }
  }
}

// Center the imported image's aspect inside the canvas.
function fitImageRect(doc: DocumentModel, img: ImportedImage) {
  const cw = doc.canvas.width
  const ch = doc.canvas.height
  const s = Math.min(cw / img.width, ch / img.height)
  const w = img.width * s
  const h = img.height * s
  return { x: (cw - w) / 2, y: (ch - h) / 2, w, h }
}

function ImageBlock({ channel, range }: { channel: ChannelId; range: { min: number; max: number; unit: string } }) {
  const doc = useStore((s) => s.doc)
  const commit = useStore((s) => s.commit)
  const live = useStore((s) => s.live)
  const beginGesture = useStore((s) => s.beginGesture)
  const endGesture = useStore((s) => s.endGesture)
  const pushToast = useStore((s) => s.pushToast)
  const img = doc.channels[channel].image

  const attach = (label: string) =>
    pickImageFile(
      (res) => {
        if (res.dataUri.length > EMBED_WARN_BYTES) {
          pushToast('Large image embedded: exports and autosaves will grow accordingly', 'warn')
        }
        commit(label, (d) => {
          const ch = d.channels[channel]
          const defs = imageDefaults(channel, ch.base)
          const prev = ch.image
          ch.image = {
            enabled: true,
            dataUri: res.dataUri,
            rect: prev ? prev.rect : fitImageRect(d, res),
            invert: prev?.invert ?? false,
            contrast: prev?.contrast ?? 0,
            valueLo: prev?.valueLo ?? defs.lo,
            valueHi: prev?.valueHi ?? defs.hi,
          }
        })
      },
      () => pushToast('Could not read that image', 'error'),
    )

  if (!img) {
    return (
      <div className="row">
        <button className="icon-btn" onClick={() => attach('Set image controller')} title="Photo luminance drives this channel inside a rectangle">
          <IconImport size={14} />
          Image as controller
        </button>
      </div>
    )
  }

  const up = (label: string, fn: (i: NonNullable<typeof img>) => void) =>
    commit(label, (d) => {
      const target = d.channels[channel].image
      if (target) fn(target)
    })

  return (
    <div className="image-block">
      <div className="controller-head">
        <span className="controller-kind">
          <IconImport size={13} />
          image drive
        </span>
        <label className="mini">
          <input
            type="checkbox"
            checked={img.enabled}
            onChange={(e) => up('Toggle image controller', (i) => (i.enabled = e.target.checked))}
          />
          on
        </label>
        <button className="quiet icon-btn" onClick={() => attach('Replace controller image')} title="Replace image">
          <IconImport size={13} />
        </button>
        <button
          className="danger icon-btn quiet"
          aria-label="Remove image controller"
          onClick={() =>
            commit('Remove image controller', (d) => {
              d.channels[channel].image = null
            })
          }
        >
          <IconTrash size={13} />
        </button>
      </div>
      <ValueField
        label="Dark value"
        compact
        value={img.valueLo}
        min={range.min}
        max={range.max}
        unit={range.unit}
        title="Channel value where the image is black"
        onCommit={(v) => up('Set image dark value', (i) => (i.valueLo = v))}
      />
      <ValueField
        label="Light value"
        compact
        value={img.valueHi}
        min={range.min}
        max={range.max}
        unit={range.unit}
        title="Channel value where the image is white"
        onCommit={(v) => up('Set image light value', (i) => (i.valueHi = v))}
      />
      <SliderField
        label="Contrast"
        value={img.contrast}
        min={-1}
        max={1}
        step={0.05}
        onStart={() => beginGesture('Adjust image contrast')}
        onEnd={endGesture}
        onLive={(v) =>
          live((d) => {
            const i = d.channels[channel].image
            if (i) i.contrast = v
          })
        }
      />
      <label className="mini">
        <input
          type="checkbox"
          checked={img.invert}
          onChange={(e) => up('Toggle image invert', (i) => (i.invert = e.target.checked))}
        />
        Invert
      </label>
      <div className="row batch-row">
        <ValueField label="X" compact value={img.rect.x} min={-2000} max={2000} onCommit={(v) => up('Move image rect', (i) => (i.rect.x = v))} />
        <ValueField label="Y" compact value={img.rect.y} min={-2000} max={2000} onCommit={(v) => up('Move image rect', (i) => (i.rect.y = v))} />
      </div>
      <div className="row batch-row">
        <ValueField label="W" compact value={img.rect.w} min={1} max={4000} onCommit={(v) => up('Resize image rect', (i) => (i.rect.w = v))} />
        <ValueField label="H" compact value={img.rect.h} min={1} max={4000} onCommit={(v) => up('Resize image rect', (i) => (i.rect.h = v))} />
      </div>
    </div>
  )
}

function BackgroundSection() {
  const t = useT()
  const doc = useStore((s) => s.doc)
  const commit = useStore((s) => s.commit)
  const live = useStore((s) => s.live)
  const beginGesture = useStore((s) => s.beginGesture)
  const endGesture = useStore((s) => s.endGesture)
  const pushToast = useStore((s) => s.pushToast)
  const bg = doc.background

  const attach = (label: string) =>
    pickImageFile(
      (res) => {
        if (res.dataUri.length > EMBED_WARN_BYTES) {
          pushToast('Large image embedded: exports and autosaves will grow accordingly', 'warn')
        }
        commit(label, (d) => {
          const prev = d.background
          const width = prev?.width ?? d.canvas.width
          d.background = {
            dataUri: res.dataUri,
            opacity: prev?.opacity ?? 0.5,
            width,
            x: prev?.x ?? 0,
            y: prev?.y ?? 0,
            visible: true,
          }
        })
      },
      () => pushToast('Could not read that image', 'error'),
    )

  const up = (label: string, fn: (b: NonNullable<typeof bg>) => void) =>
    commit(label, (d) => {
      if (d.background) fn(d.background)
    })

  return (
    <Section title={t('section.background', 'Background image')}>
      {!bg && (
        <>
          <button className="icon-btn" onClick={() => attach('Set background image')}>
            <IconImport size={14} />
            Add background image
          </button>
          <p className="hint">A reference photo under the pattern, visible through the holes.</p>
        </>
      )}
      {bg && (
        <>
          <div className="row">
            <label className="mini">
              <input
                type="checkbox"
                checked={bg.visible}
                onChange={(e) => up('Toggle background', (b) => (b.visible = e.target.checked))}
              />
              visible
            </label>
            <button className="quiet icon-btn" onClick={() => attach('Replace background image')} title="Replace image">
              <IconImport size={13} />
            </button>
            <button
              className="danger icon-btn quiet"
              aria-label="Remove background image"
              onClick={() =>
                commit('Remove background image', (d) => {
                  d.background = null
                })
              }
            >
              <IconTrash size={13} />
            </button>
          </div>
          <SliderField
            label="Opacity"
            value={bg.opacity}
            min={0}
            max={1}
            step={0.05}
            onStart={() => beginGesture('Adjust background opacity')}
            onEnd={endGesture}
            onLive={(v) =>
              live((d) => {
                if (d.background) d.background.opacity = v
              })
            }
          />
          <ValueField label="Width" value={bg.width} min={1} max={4000} unit="mm" onCommit={(v) => up('Resize background', (b) => (b.width = v))} />
          <div className="row batch-row">
            <ValueField label="X" compact value={bg.x} min={-2000} max={2000} onCommit={(v) => up('Move background', (b) => (b.x = v))} />
            <ValueField label="Y" compact value={bg.y} min={-2000} max={2000} onCommit={(v) => up('Move background', (b) => (b.y = v))} />
          </div>
        </>
      )}
    </Section>
  )
}

function ExportSection() {
  const t = useT()
  const doc = useStore((s) => s.doc)
  const thicknessOps = useFieldOps('Set thickness', (d, v) => {
    d.export.thickness = v
  })
  const toleranceOps = useFieldOps('Set tolerance', (d, v) => {
    d.export.tolerance = v
  })
  return (
    <Section title={t('section.export', 'Export settings')}>
      <ValueField label="Thickness" value={doc.export.thickness} min={0.2} max={100} unit="mm" {...thicknessOps} />
      <ValueField
        label="Tolerance"
        value={doc.export.tolerance}
        min={0.005}
        max={0.5}
        step={0.005}
        unit="mm"
        {...toleranceOps}
      />
      <p className="hint">
        Chord tolerance for tessellating curves. It drives the preview, STL and clipped STEP
        geometry identically.
      </p>
    </Section>
  )
}

export function makeController(
  kind: ControllerKind,
  channel: ChannelId,
  a: Vec2,
  b: Vec2,
  baseValue: number,
  nodes: Vec2[] = [],
): Controller {
  return {
    id: newControllerId(),
    kind,
    a,
    b,
    nodes,
    value: channel === 'angle' ? 45 : channel === 'shape' ? 1 : baseValue * 1.5,
    radius: 25,
    falloff: 'smooth',
    hardEdge: false,
    enabled: true,
    singleSide: 'off',
    infinite: false,
    syncGroup: null,
  }
}
